import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Stripe, PaymentSheetEventsEnum } from '@capacitor-community/stripe';
import { WhatTheSheetSaid } from '../core/domains/payments/domain/what-went-wrong-with-the-payment';
import { environment } from '../../environments/environment';
import { LanguageService } from '../core/services/language.service';
import type { CreatePaymentSheetOption } from '@capacitor-community/stripe';
import { theErrorMessage } from '../core/utils/what-an-error-carries';

/**
 * Stripe Adapter
 *
 * Wraps @capacitor-community/stripe plugin to provide a consistent interface
 * for payment processing across web and native platforms.
 *
 * Features:
 * - Native payment sheet (iOS/Android)
 * - Apple Pay & Google Pay support
 * - Web fallback for browser testing
 */
@Injectable({
  providedIn: 'root',
})
export class StripeAdapter {
  private isInitialized = false;
  private readonly _languageService = inject(LanguageService);

  /**
   * Initialize Stripe with publishable key
   * Should be called once at app startup
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('[StripeAdapter] Already initialized');
      return;
    }

    // Every environment defines its own publishable key (see i-environment.ts),
    // so there is no placeholder fallback: a misconfigured build is caught by
    // the warning below rather than silently initialised with a fake key.
    const publishableKey = environment.stripePublishableKey;

    if (!publishableKey || !publishableKey.startsWith('pk_')) {
      console.warn('[StripeAdapter] Missing or malformed publishable key — check the environment config.');
    }

    if (Capacitor.isNativePlatform()) {
      try {
        await Stripe.initialize({
          publishableKey,
        });
        this.isInitialized = true;
      } catch (error) {
        console.error('[StripeAdapter] Failed to initialize Stripe:', error);
        throw error;
      }
    } else {
      // Web platform - Stripe.js will be loaded separately if needed
      // For now, we'll handle web payments differently
      console.warn('[StripeAdapter] Stripe native plugin not available on web platform');
      this.isInitialized = true;
    }
  }

  /**
   * Present the Stripe payment sheet with the given client secret
   *
   * @param clientSecret - Stripe PaymentIntent client secret from backend
   * @param customerId - Optional Stripe Customer ID (required for "Save Card" checkbox)
   * @param customerEphemeralKeySecret - Optional Stripe ephemeral key secret (required for "Save Card" checkbox)
   * @param locale - Optional locale ('fr' or 'en'), defaults to app language preference
   * @returns Promise that resolves when payment is completed or rejected
   */
  async presentPaymentSheet(
    clientSecret: string,
    customerId?: string,
    customerEphemeralKeySecret?: string,
    locale?: string,
  ): Promise<{
    success: boolean;
    said?: WhatTheSheetSaid;
    error?: string;
  }> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    const isNative = Capacitor.isNativePlatform();

    if (!isNative) {
      // Web fallback - for development/testing
      // In production, you might want to use Stripe Elements or redirect
      console.warn('[StripeAdapter] Native payment sheet not available on web platform');
      return {
        success: false,
        error: 'Native payment sheet not available on web platform. Please test on iOS or Android device.',
      };
    }

    try {
      // Validate client secret format before creating payment sheet
      if (!clientSecret || typeof clientSecret !== 'string') {
        console.error('[StripeAdapter] Invalid client secret: missing or not a string');
        return {
          success: false,
          error: 'Invalid payment configuration. Please try again.',
        };
      }

      // Client secret should start with 'pi_' and contain '_secret_'
      if (!clientSecret.startsWith('pi_') || !clientSecret.includes('_secret_')) {
        console.error('[StripeAdapter] Invalid client secret format (expected pi_..._secret_...)');
        return {
          success: false,
          error: 'Invalid payment configuration. Please try again.',
        };
      }

      const currentLocale = locale || this._languageService.getCurrentLang();

      // NOTE: Stripe Capacitor plugin does NOT currently support a locale
      // parameter, so the Payment Sheet uses the device's system language. The
      // option is intentionally omitted here; see mapLocaleToStripe for the
      // mapping to reinstate if the plugin adds support.
      // https://github.com/stripe/stripe-react-native/issues/243
      const paymentSheetOptions: CreatePaymentSheetOption = {
        paymentIntentClientSecret: clientSecret,
        merchantDisplayName: 'Smatchy',
      };

      console.log('[StripeAdapter] Locale parameter not supported by Stripe Capacitor plugin');
      console.log('[StripeAdapter] Payment Sheet will use device system language:', navigator.language);
      console.log('[StripeAdapter] App language preference:', currentLocale, '(not used by Payment Sheet)');

      // Add customerId and ephemeral key if provided (enables "Save Card" checkbox)
      // Both must be non-empty strings for the checkbox to appear
      const hasValidCustomerId = customerId && typeof customerId === 'string' && customerId.trim().length > 0;
      const hasValidEphemeralKey =
        customerEphemeralKeySecret &&
        typeof customerEphemeralKeySecret === 'string' &&
        customerEphemeralKeySecret.trim().length > 0;

      // Both must be present for the "Save Card" checkbox to appear.
      if (hasValidCustomerId && hasValidEphemeralKey) {
        paymentSheetOptions.customerId = customerId;
        paymentSheetOptions.customerEphemeralKeySecret = customerEphemeralKeySecret;
      }

      // Always create a fresh Payment Sheet for each payment so a stale
      // configuration is never reused.
      await Stripe.createPaymentSheet(paymentSheetOptions);

      const result = await Stripe.presentPaymentSheet();

      // PaymentSheetResultInterface enum values: Completed | Canceled | Failed | paymentSheetFailed
      if (result.paymentResult === PaymentSheetEventsEnum.Completed) {
        console.log('[StripeAdapter] Payment completed successfully');
        return { success: true, said: 'completed' as WhatTheSheetSaid };
      } else if (result.paymentResult === PaymentSheetEventsEnum.Canceled) {
        console.log('[StripeAdapter] Payment was canceled by user');
        // A VALUE, not a sentence. Somebody closing the sheet and the sheet
        // breaking used to arrive as two English strings, which is why the
        // state could not tell an abandoned basket from a fault of ours.
        return {
          success: false,
          said: 'cancelled' as WhatTheSheetSaid,
          error: 'Payment was canceled',
        };
      } else if (result.paymentResult === PaymentSheetEventsEnum.Failed) {
        console.log('[StripeAdapter] Payment failed');
        return {
          success: false,
          said: 'failed' as WhatTheSheetSaid,
          error: 'Payment failed',
        };
      } else if (result.paymentResult === 'paymentSheetFailed') {
        // paymentSheetFailed means the payment sheet itself failed to present
        // This could be due to invalid client secret, network issues, or configuration problems
        console.error('[StripeAdapter] Payment sheet failed to present');
        console.error('[StripeAdapter] This usually indicates:');
        console.error('[StripeAdapter] - Invalid or expired client secret');
        console.error('[StripeAdapter] - Network connectivity issues');
        console.error('[StripeAdapter] - Stripe configuration problems');
        console.error('[StripeAdapter] - Payment intent not properly created on backend');

        return {
          success: false,
          said: 'would-not-open' as WhatTheSheetSaid,
          error: 'Payment sheet failed to open. Please check your connection and try again.',
        };
      }

      // Fallback for unknown payment results
      console.warn('[StripeAdapter] Unknown payment result:', result.paymentResult);
      return {
        success: false,
        said: 'something-else' as WhatTheSheetSaid,
        error: `Unknown payment result: ${result.paymentResult}`,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: theErrorMessage(error) || 'Payment processing failed',
      };
    }
  }

  /**
   * Check if Apple Pay is available on the device
   */
  async isApplePayAvailable(): Promise<boolean> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      return false;
    }

    try {
      // isApplePayAvailable() returns Promise<void> - it doesn't throw if available
      await Stripe.isApplePayAvailable();
      return true;
    } catch {
      // If it throws, Apple Pay is not available
      return false;
    }
  }

  /**
   * Check if Google Pay is available on the device
   */
  async isGooglePayAvailable(): Promise<boolean> {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      return false;
    }

    try {
      // isGooglePayAvailable() returns Promise<void> - it doesn't throw if available
      await Stripe.isGooglePayAvailable();
      return true;
    } catch {
      // If it throws, Google Pay is not available
      return false;
    }
  }

  /**
   * Present the Stripe payment sheet with Setup Intent for adding cards
   *
   * @param setupIntentClientSecret - Stripe Setup Intent client secret from backend
   * @param customerId - Stripe Customer ID (required for "Save Card" checkbox)
   * @param customerEphemeralKeySecret - Stripe ephemeral key secret (required for "Save Card" checkbox)
   * @param locale - Optional locale ('fr' or 'en'), defaults to app language preference
   * @returns Promise that resolves with setupIntentId when setup is completed
   */
  async presentSetupIntentSheet(
    setupIntentClientSecret: string,
    customerId: string,
    customerEphemeralKeySecret: string,
    locale?: string,
  ): Promise<{
    success: boolean;
    setupIntentId?: string;
    error?: string;
  }> {
    console.log('[StripeAdapter] presentSetupIntentSheet called');

    if (!this.isInitialized) {
      console.log('[StripeAdapter] Not initialized, initializing now...');
      await this.initialize();
    }

    const platform = Capacitor.getPlatform();
    const isNative = Capacitor.isNativePlatform();
    console.log('[StripeAdapter] Platform:', platform, 'isNative:', isNative);

    if (!isNative) {
      console.warn('[StripeAdapter] Native payment sheet not available on web platform');
      return {
        success: false,
        error: 'Native payment sheet not available on web platform. Please test on iOS or Android device.',
      };
    }

    try {
      if (!setupIntentClientSecret || typeof setupIntentClientSecret !== 'string') {
        console.error('[StripeAdapter] Invalid setup intent client secret');
        return {
          success: false,
          error: 'Invalid payment configuration. Please try again.',
        };
      }

      if (!setupIntentClientSecret.startsWith('seti_') || !setupIntentClientSecret.includes('_secret_')) {
        console.error('[StripeAdapter] Invalid setup intent client secret format');
        return {
          success: false,
          error: 'Invalid payment configuration. Please try again.',
        };
      }

      // For Setup Intent, we want to show card input form, not saved payment methods
      // When customerId is provided to Payment Sheet, Stripe shows saved payment methods
      // To show card input only, we should NOT provide customerId to the Payment Sheet
      // The Setup Intent is still created with the customer on the backend, so the payment method
      // can be attached to the customer after confirmation
      const hasValidCustomerId = customerId && typeof customerId === 'string' && customerId.trim().length > 0;
      const hasValidEphemeralKey =
        customerEphemeralKeySecret &&
        typeof customerEphemeralKeySecret === 'string' &&
        customerEphemeralKeySecret.trim().length > 0;

      // Note: We receive customerId and ephemeralKeySecret from backend, but we don't pass them
      // to the Payment Sheet to prevent showing saved payment methods
      // The Setup Intent is already associated with the customer on the backend
      if (!hasValidCustomerId || !hasValidEphemeralKey) {
        console.error('[StripeAdapter] Missing customerId or ephemeral key for Setup Intent');
        return {
          success: false,
          error: 'Invalid payment configuration. Please try again.',
        };
      }

      const currentLocale = locale || this._languageService.getCurrentLang();
      const stripeLocale = this.mapLocaleToStripe(currentLocale);

      // Don't include customerId and ephemeralKeySecret in Payment Sheet options
      // This ensures Payment Sheet shows card input form instead of saved payment methods
      // The Setup Intent is already created with the customer on the backend
      // NOTE: Stripe Capacitor plugin does NOT currently support locale parameter
      // Payment Sheet will use device's system language, not app language preference
      const paymentSheetOptions: CreatePaymentSheetOption = {
        setupIntentClientSecret: setupIntentClientSecret,
        // NOTE: Intentionally NOT including customerId and customerEphemeralKeySecret
        // This prevents Payment Sheet from showing saved payment methods
        // The Setup Intent is still associated with the customer on the backend
        merchantDisplayName: 'Smatchy',
        // locale: stripeLocale, // Currently not supported by @capacitor-community/stripe plugin
        // Payment Sheet will use device's system language instead
      };

      console.log(
        '[StripeAdapter] Setup Intent Payment Sheet configured to show card input only (customerId not included to prevent showing saved cards)',
      );
      console.log('[StripeAdapter] Locale parameter not supported by Stripe Capacitor plugin');
      console.log('[StripeAdapter] Payment Sheet will use device system language:', navigator.language);
      console.log('[StripeAdapter] App language preference:', currentLocale, '(not used by Payment Sheet)');

      await Stripe.createPaymentSheet(paymentSheetOptions);
      console.log('[StripeAdapter] Setup Intent payment sheet created, presenting...');

      const result = await Stripe.presentPaymentSheet();
      console.log('[StripeAdapter] Setup Intent payment sheet result:', JSON.stringify(result, null, 2));

      if (result.paymentResult === PaymentSheetEventsEnum.Completed) {
        console.log('[StripeAdapter] Setup Intent completed successfully');
        const setupIntentId = this.extractSetupIntentId(setupIntentClientSecret);
        return {
          success: true,
          setupIntentId,
        };
      } else if (result.paymentResult === PaymentSheetEventsEnum.Canceled) {
        console.log('[StripeAdapter] Setup Intent was canceled by user');
        return {
          success: false,
          error: 'Setup was canceled',
        };
      } else if (result.paymentResult === PaymentSheetEventsEnum.Failed) {
        console.log('[StripeAdapter] Setup Intent failed');
        return {
          success: false,
          error: 'Setup failed',
        };
      } else if (result.paymentResult === 'paymentSheetFailed') {
        console.error('[StripeAdapter] Setup Intent payment sheet failed to present');
        return {
          success: false,
          error: 'Payment sheet failed to open. Please check your connection and try again.',
        };
      }

      console.warn('[StripeAdapter] Unknown setup intent result:', result.paymentResult);
      return {
        success: false,
        error: `Unknown setup result: ${result.paymentResult}`,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: theErrorMessage(error) || 'Setup processing failed',
      };
    }
  }

  private mapLocaleToStripe(locale: string): string {
    const localeMap: Record<string, string> = {
      fr: 'fr',
      en: 'en',
    };
    return localeMap[locale] || 'en';
  }

  private extractSetupIntentId(setupIntentClientSecret: string): string {
    const parts = setupIntentClientSecret.split('_secret_');
    if (parts.length > 0) {
      return parts[0];
    }
    return '';
  }
}
